import { Badge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { comoEventoDeCambio } from '@/shared/lib/opcionesDeSelect'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { useState, useEffect, useCallback } from 'react'
import { Checkbox } from '@/shared/components/Checkbox'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { ArrowLeft, Pen, Save, X, User, Trash2 } from 'lucide-react'
import { apiService, type UserProfile, type ApiError } from '@/services/api'
import { EliminacionBloqueada } from '@/shared/components/EliminacionBloqueada'
import { ConfirmarEliminacion } from '@/shared/components/ConfirmarEliminacion'
import AuditHistory from '@/features/configuracion/components/AuditHistory'
import { getAreasCatalog, type Area } from '@/features/configuracion/api/areaService'
import type { Role } from '@/types/rbac.types'
import { hasRole, userHasPermission } from '@/shared/lib/roleUtils'
import { useAuth } from '@/shared/hooks/useAuth'

const UserDetail = () => {
  const navigate = useNavigate()
  const { id: userId = '' } = useParams<{ id: string }>()
  const location = useLocation()
  const { user } = useAuth();
  const esSuperadmin = hasRole(user, 'MASTER')
  const [userData, setUserData] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // La edición vive en su propia URL (/users/:id/editar). Antes era un estado
  // local, así que el botón Atrás del navegador salía al listado en vez de
  // regresar a la consulta: para el usuario, editar es otra pantalla.
  const isEditing = location.pathname.endsWith('/editar')
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [preguntandoSiBorrar, setPreguntandoSiBorrar] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Motivo por el que el servidor rechazo la baja, si la rechazo. */
  const [bloqueado, setBloqueado] = useState<string | null>(null)
  const [primaryRole, setPrimaryRole] = useState<{ key: string; name: string } | null>(null)
  
  // Estados para áreas
  const [areas, setAreas] = useState<Area[]>([])
  const [isLoadingAreas, setIsLoadingAreas] = useState(false)

  // Estados para roles
  const [availableRoles, setAvailableRoles] = useState<Role[]>([])
  const [_selectedRoleKey, setSelectedRoleKey] = useState<string>('')
  const [isLoadingRoles, setIsLoadingRoles] = useState(true)
  
  // Datos del formulario para edición (solo campos editables)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    isActive: true,
    areaId: null as number | null,
    role: '',
    isAreaManager: false,
    canAssignSalaries: false,
    canBypassSupplierDocumentValidation: false,
    receivesToolDecommissionEmail: false,
    receivesSupplierApprovalEmail: false
  })

  const loadAvailableRoles = async () => {
    setIsLoadingRoles(true)
    try {
      const roles = await apiService.getRolesCatalog()
      setAvailableRoles(roles)
      // Set first role as default if available
      if (roles.length > 0) {
        setSelectedRoleKey(roles[0].key)
      }
    } catch (err) {
      const apiError = err as ApiError
      console.error('Error loading available roles:', apiError)
      setError('Error al cargar los roles disponibles')
    } finally {
      setIsLoadingRoles(false)
    }
  }
  const loadAreas = async () => {
    setIsLoadingAreas(true)
    try {
      const areasData = await getAreasCatalog()
      const activeAreas = areasData.filter(area => area.activa)
      setAreas(activeAreas)
    } catch (err) {
      console.error('Error al cargar áreas:', err)
    } finally {
      setIsLoadingAreas(false)
    }
  }

  const updatePrimaryRole = useCallback((roles?: Role[] | null) => {
    if (roles && roles.length > 0) {
      const [role] = roles
      setPrimaryRole({
        key: role.key,
        name: role.name || role.key
      })
    } else {
      setPrimaryRole(null)
    }
  }, [])

  const loadUserDetail = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const userData = await apiService.getUserById(userId)
      setUserData(userData)
      let rbacData

      try {
        rbacData = await apiService.getUserRBAC(userId)
        updatePrimaryRole(rbacData.roles)
      } catch (rbacError) {
        console.error('Error al cargar roles del usuario para info personal:', rbacError)
        updatePrimaryRole()
      }
       // Inicializar datos del formulario (solo campos editables)
        setFormData({
          firstName: userData.firstName,
          lastName: userData.lastName,
          isActive: userData.isActive,
          areaId: userData.areaId || null,
          role: rbacData?.roles[0]?.key ?? '',
          canAssignSalaries: userData?.canAssignSalaries ?? false,
          isAreaManager: userData?.isAreaManager ?? false,
          canBypassSupplierDocumentValidation:
            userData?.canBypassSupplierDocumentValidation ?? false,
          receivesToolDecommissionEmail:
            userData?.receivesToolDecommissionEmail ?? false,
          receivesSupplierApprovalEmail:
            userData?.receivesSupplierApprovalEmail ?? false
        })
    } catch (err) {
      const apiError = err as ApiError
      const message = Array.isArray(apiError.message)
        ? apiError.message.join(', ')
        : apiError.message || 'Error al cargar detalles de usuario'
      setError(message)
      console.error('Error al cargar detalles del usuario:', apiError)
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const handleEdit = () => {
    navigate(`/users/${userId}/editar`)
    setError(null)
  }

  const handleCancel = () => {
    navigate(`/users/${userId}`)
    setError(null)
    // Restablecer datos del formulario a valores originales (solo campos editables)
    if (userData) {
      setFormData({
        firstName: userData.firstName,
        lastName: userData.lastName,
        isActive: userData.isActive,
        areaId: userData.areaId || null,
        role: primaryRole?.key || '',
        canAssignSalaries: userData?.canAssignSalaries ?? false,
        isAreaManager: userData?.isAreaManager ?? false,
        canBypassSupplierDocumentValidation:
          userData?.canBypassSupplierDocumentValidation ?? false,
        receivesToolDecommissionEmail:
          userData?.receivesToolDecommissionEmail ?? false,
        receivesSupplierApprovalEmail:
          userData?.receivesSupplierApprovalEmail ?? false
      })
    }
  }

  const handleDelete = () => {
    if (userData) setPreguntandoSiBorrar(true)
  }

  /** Ya confirmado en el modal; el window.confirm congelaba la pestana. */
  const eliminaConfirmado = async () => {
    if (!userData) return
    setPreguntandoSiBorrar(false)
    setIsDeleting(true)
    setError(null)

    try {
      await apiService.deleteUser(userId)
      // Llamar al callback para notificar al componente padre
      navigate('/users')
    } catch (err) {
      const apiError = err as ApiError
      const message = Array.isArray(apiError.message)
        ? apiError.message.join(', ')
        : apiError.message || 'Error al eliminar usuario'

      // 409: algo lo retiene y hay que resolverlo antes. Va en modal porque
      // pide una accion, no solo enterarse.
      if (apiError.statusCode === 409) {
        setBloqueado(message)
      } else {
        setError(message)
      }
      console.error('Error al eliminar usuario:', apiError)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)

    try {
      // Preparar los datos de actualización (incluye nombre, estado, rol y área)
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        isActive: formData.isActive,
        role: formData.role,
        areaId: formData.areaId ? Number(formData.areaId) : undefined,
        canAssignSalaries: formData?.canAssignSalaries ?? false,
        isAreaManager: formData?.isAreaManager ?? false,
        canBypassSupplierDocumentValidation:
          formData?.canBypassSupplierDocumentValidation ?? false,
        receivesToolDecommissionEmail:
          formData?.receivesToolDecommissionEmail ?? false,
        receivesSupplierApprovalEmail:
          formData?.receivesSupplierApprovalEmail ?? false
      }

      // Sin esta llamada la pantalla decia que guardaba y no guardaba nada. Se
      // perdio el 17/08 en una limpieza de declaraciones muertas: la variable
      // que recibia el resultado no se usaba y se borro el renglon completo, no
      // solo la asignacion. Por eso ahora no se asigna.
      await apiService.updateUser(userId, updateData)

      await loadUserDetail()

      navigate(`/users/${userId}`)
    } catch (err) {
      const apiError = err as ApiError
      const message = Array.isArray(apiError.message)
        ? apiError.message.join(', ')
        : apiError.message || 'Error al actualizar usuario'
      setError(message)
      console.error('Error al actualizar usuario:', apiError)
    } finally {
      setIsSaving(false)
    }
  }

    // Cargar datos del usuario al montar el componente
  // Los datos del usuario que se consulta dependen del id de la URL, no de
  // quien esta en sesion. Con `user` en las dependencias, la pantalla se
  // recargaba sola en cuanto la sesion terminaba de resolverse.
  useEffect(() => {
    if (!userId) return
    loadUserDetail()
    loadAreas()
    loadAvailableRoles()
  }, [userId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Cargando detalles del usuario...</p>
        </div>
      </div>
    )
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Usuario no encontrado</p>
        <button
          onClick={() => navigate(-1 as any)}
          className={estiloDeBoton('secundario', 'mt-4 mx-auto')}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver a Usuarios</span>
        </button>
      </div>
    )
  }

  const resolvedRoleKey = (primaryRole?.key || '').toUpperCase()
  const resolvedRoleLabel = primaryRole?.name || (resolvedRoleKey ?? 'N/A')

  return (
    <div className="space-y-6">
      <div className="w-full max-w-none">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1 as any)}
            className={estiloDeBoton('secundario', 'mb-4')}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Volver</span>
          </button>

          <div>
            <TituloDePantalla nivel="interna" className="mb-1">
              {isEditing ? 'Editar Usuario' : 'Detalles del Usuario'}
            </TituloDePantalla>
            <p className="text-sm text-[#6B7280]">
              {isEditing ? 'Actualiza la información del usuario' : 'Información del usuario en el sistema'}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Información del usuario */}
        <div className="bg-white rounded-xl border border-[#E0E0E0] mb-6">
          <div className="px-6 py-4 border-b border-[#E0E0E0]">
            <h2 className="text-base font-semibold text-[#1E1E1F]">Información del usuario</h2>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre<span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Ingresa el nombre"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                ) : (
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                    {userData.firstName}
                  </p>
                )}
              </div>

              {/* Apellido */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apellido<span className="text-red-500">*</span>
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Ingresa el apellido"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                ) : (
                  <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                    {userData.lastName}
                  </p>
                )}
              </div>

              {/* Correo Electrónico */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección de email<span className="text-red-500">*</span>
                </label>
                <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-500">
                  {userData.email}
                </div>
              </div>

               {/* Role Selection */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                Rol de Usuario *
              </label>
              
              {isEditing && isLoadingRoles ? (
                <div className="flex items-center justify-center py-2 px-3 border border-gray-300 rounded-lg bg-gray-50">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"></div>
                  <span className="text-sm text-gray-600">Cargando roles...</span>
                </div>
              ) : isEditing && availableRoles.length === 0 ? (
                <div className="py-2 px-3 border border-red-300 rounded-lg bg-red-50 text-sm text-red-700">
                  No hay roles disponibles
                </div>
              ) : isEditing && !isLoadingRoles && availableRoles.length > 0 ? (
                <SelectBuscable
                  variante="formulario"
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={(valor) => handleInputChange(comoEventoDeCambio('role', valor))}
                  required
                  placeholder="Selecciona un rol"
                  placeholderDeBusqueda="Buscar rol..."
                  opciones={availableRoles.map((role) => ({ value: role.key, label: role.name }))}
                />
              ) : ( 
              <>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <Badge color="morado">{resolvedRoleLabel}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      El rol determina los permisos y accesos del usuario en el sistema
                    </p>
                  </>
                )}
             
            </div>

              {/* Área asignada */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departamento/Área
                </label>
                {isEditing ? (
                  <>
                    <SelectBuscable
                      variante="formulario"
                      name="areaId"
                      value={String(formData.areaId || '')}
                      onChange={(valor) => handleInputChange(comoEventoDeCambio('areaId', valor))}
                      disabled={isLoadingAreas}
                      placeholder="Seleccionar"
                      placeholderDeBusqueda="Buscar área..."
                      opciones={areas.map((area) => ({ value: String(area.id), label: area.nombre }))}
                    />
                    {isLoadingAreas && (
                      <p className="mt-1 text-xs text-gray-500">Cargando áreas...</p>
                    )}
                  </>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                    {userData.areaId ? (
                      areas.find(a => a.id === userData.areaId)?.nombre || 'Departamento/Área no encontrada'
                    ) : (
                      <span className="text-gray-500">Sin asignar</span>
                    )}
                  </div>
                )}
              </div>

              {/* Estado (solo en modo edición) */}
              {isEditing && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado del usuario
                  </label>
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    <span className="text-sm text-gray-700">Usuario Activo</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Los usuarios inactivos no podrán acceder al sistema
                  </p>
                </div>
              )}

              {/* Estado (solo en modo vista) */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <div className="px-3 py-2">
                    <Badge color={userData.isActive ? 'verde' : 'gris'}>
                      {userData.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Permissions ExtraOrdinaries Section - Separate Card */}
        <div className="bg-white rounded-xl border border-[#E0E0E0] overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-[#E0E0E0]">
            <h2 className="text-base font-semibold text-[#1E1E1F]">
              Permisos extraordinarios
            </h2>
          </div>

          <div className="p-6">
            {/* Usamos flex-col para forzar una sola columna vertical */}
            <div className="flex flex-col gap-6">
              {/* Checkbox 1: Gerente de departamento/área */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <Checkbox
                    id="isAreaManager"
                    name="isAreaManager"
                    disabled={!isEditing}
                    checked={formData.isAreaManager}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="isAreaManager"
                    className="font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Gerente de departamento/área
                  </label>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Permite al usuario realizar la preaprobación de solicitudes
                    de personal de su área, rechazarlas y otorgar la
                    aprobación definitiva una vez que el salario ha sido
                    asignado. Este rol participa como filtro final antes de
                    que la vacante quede formalmente autorizada.
                  </p>
                </div>
              </div>

              {/* Checkbox 2: Asignar Sueldos */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <Checkbox
                    id="canAssignSalaries"
                    name="canAssignSalaries"
                    disabled={!isEditing}
                    checked={formData.canAssignSalaries}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="canAssignSalaries"
                    className="font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Asignar sueldos
                  </label>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Permite al usuario definir, modificar y autorizar sueldos y
                    compensaciones dentro del sistema, de acuerdo con las
                    políticas establecidas por la empresa.
                  </p>
                </div>
              </div>

              {/* Checkbox 3: Bypass de expediente de proveedor (SP-212/213) */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <Checkbox
                    id="canBypassSupplierDocumentValidation"
                    name="canBypassSupplierDocumentValidation"
                    disabled={!isEditing}
                    checked={formData.canBypassSupplierDocumentValidation}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="canBypassSupplierDocumentValidation"
                    className="font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Autorizar bypass proveedor
                  </label>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Permite al usuario autorizar que un proveedor opere aunque su
                    expediente esté incompleto. Cada excepción queda registrada
                    con su usuario, la fecha y el motivo.
                  </p>
                </div>
              </div>

              {/* Checkbox 4: Aviso de baja de herramienta (CR-02) */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <Checkbox
                    id="receivesToolDecommissionEmail"
                    name="receivesToolDecommissionEmail"
                    disabled={!isEditing}
                    checked={formData.receivesToolDecommissionEmail}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label
                    htmlFor="receivesToolDecommissionEmail"
                    className="font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Recibir aviso de baja de herramienta
                  </label>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Le llega un correo cada vez que se da de baja una herramienta,
                    con sus datos y el motivo. El aviso también le llega a quien
                    tenía la herramienta a su cargo. No habilita dar de baja.
                  </p>
                </div>
              </div>

              {/* Checkbox 5: Aviso de alta de proveedor a Tesorería (CC-04, SP-254).
                  Solo lo ve el superadministrador; el back acepta la bandera de
                  quien ya puede editar usuarios. */}
              {esSuperadmin && (
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <Checkbox
                      id="receivesSupplierApprovalEmail"
                      name="receivesSupplierApprovalEmail"
                      disabled={!isEditing}
                      checked={formData.receivesSupplierApprovalEmail}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label
                      htmlFor="receivesSupplierApprovalEmail"
                      className="font-medium text-gray-700 cursor-pointer select-none"
                    >
                      Recibe aviso de alta de proveedor (Tesorería)
                    </label>
                    <p className="text-gray-500 text-xs mt-0.5">
                      Le llega un correo cada vez que un proveedor queda
                      aprobado, con los datos del proveedor y de su cuenta
                      bancaria. Solo le llega si el usuario está activo.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="flex items-center justify-end gap-3">
          {!isEditing ? (
            <>
              {userHasPermission(
                "generales.usuarios.base.delete",
                user?.rbac?.effectivePermissions || [],
              ) && (
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className={estiloDeBoton('peligro')}
                >
                  {isDeleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                      <span>Eliminando...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Eliminar Usuario</span>
                    </>
                  )}
                </button>
              )}
              {userHasPermission(
                "generales.usuarios.base.update",
                user?.rbac?.effectivePermissions || [],
              ) && (
                <button
                  onClick={handleEdit}
                  className={estiloDeBoton('primario')}
                >
                  <Pen className="h-4 w-4" />
                  <span>Editar Usuario</span>
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              {userHasPermission(
                "generales.usuarios.base.update",
                user?.rbac?.effectivePermissions || [],
              ) && <button
                onClick={handleSave}
                disabled={isSaving}
                className={estiloDeBoton('primario')}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Guardar cambios</span>
                  </>
                )}
              </button>}
            </>
          )}
        </div>

        {/* Asignación de Área (solo en modo vista) */}
        {!isEditing && (
          <div className="mt-6">
            {/* Área ya está incluida en el card de información del usuario */}
          </div>
        )}
        
        {/* Historial de Auditoría (solo en modo vista) */}
        {!isEditing && (
          <div className="mt-6">
            <AuditHistory userId={userId} />
          </div>
        )}

        {preguntandoSiBorrar && userData && (
          <ConfirmarEliminacion
            titulo="¿Eliminar usuario?"
            nombre={userData.email}
            detalle="Se borran también sus notificaciones, sus permisos y su expediente de empleado si lo tiene. Su actividad se conserva en la bitácora."
            trabajando={isDeleting}
            onCancelar={() => setPreguntandoSiBorrar(false)}
            onConfirmar={eliminaConfirmado}
          />
        )}

        {bloqueado && (
          <EliminacionBloqueada
            nombre={
              `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim() ||
              userData?.email
            }
            motivo={bloqueado}
            onCerrar={() => setBloqueado(null)}
          />
        )}
      </div>
    </div>
  )
}

export default UserDetail
